/* The /dbf-repair page.
 *
 * No worker, and that is a measured decision rather than an oversight: a 119 MB
 * table reads whole in 95 ms and gives its first 200 rows in 13 ms, because DBF
 * records are fixed-width and there is no index to walk. Only a full sweep of
 * every record costs real time (1.1 s at that size), so rows are paged and the
 * main thread never sits still for long. The archive tool next door needs a
 * worker; this one would only be slower for it.
 *
 * What the interface must not do:
 *  - call a healthy file damaged. The header claiming the wrong number of
 *    records changes nothing for a reader that counts them from the file size,
 *    so that is stated as a fact, never as an alarm;
 *  - hide deleted rows. They are the thing people come here for;
 *  - pretend to know the encoding. When the file does not say, the guess is
 *    shown WITH its confidence and can be overridden.
 */

import { readHeader, readFields, readRecords } from './dbf-format.js'
import { diagnose, repairedView, detectCodepage, sampleTextBytes } from './dbf-repair.js'
import { openMemo, readMemoText } from './dbf-memo.js'
import { toCsv, toJson, toDbf, CSV_PRESETS } from './dbf-export.js'
import { LANGUAGE_DRIVER } from './dbf-codepages.js'
import { reportShape } from '../file-shape.js'
import { suggestOtherTool } from '../file-signature.js'

const T = window.__DBF_I18N || {}
const t = (k, fallback, vars) => {
  let s = T[k] || fallback || k
  /* Longest placeholder first. ':to' is a prefix of ':total', so substituting in
     insertion order rewrites ':total' into '177tal' — which reads as a typo in
     the translation rather than as a bug, and survives every key-parity and
     SEO check because the string is only assembled at runtime. Laravel's own
     translator sorts by length for this exact reason; the JS side has to match
     it, otherwise any future ':n'/':name' pair breaks the same silent way.
     Caught on the Arabic page by looking at a screenshot, not by a test. */
  if (vars) {
    for (const [n, v] of Object.entries(vars).sort((a, b) => b[0].length - a[0].length)) {
      s = s.split(':' + n).join(String(v))
    }
  }
  return s
}
const $ = s => document.querySelector(s)
const el = (tag, cls, text) => {
  const n = document.createElement(tag)
  if (cls) n.className = cls
  if (text != null) n.textContent = text
  return n
}
const PAGE = 200

const state = {
  bytes: null, name: '', plan: null, view: null, memo: null, memoName: null,
  cpgName: null, offset: 0, showDeleted: true, override: null, total: 0,
}

function setStatus(msg, kind = 'info') {
  const box = $('#dbf-status')
  if (!box) return
  box.hidden = !msg
  box.className = 'dbf-status lg-card dbf-status--' + kind
  box.textContent = msg || ''
}

const fmtBytes = n => {
  const u = [t('unit_b', 'B'), t('unit_kb', 'KB'), t('unit_mb', 'MB'), t('unit_gb', 'GB')]
  let i = 0, v = n
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++ }
  return `${v < 10 && i ? v.toFixed(1) : Math.round(v)} ${u[i]}`
}

function renderSummary() {
  const box = $('#dbf-summary')
  const p = state.plan
  box.innerHTML = ''
  box.hidden = false

  const chips = el('div', 'dbf-chips')
  const chip = (label, value, kind = '') => {
    const c = el('span', 'dbf-chip' + (kind ? ' dbf-chip--' + kind : ''))
    c.append(el('b', null, label), document.createTextNode(' '), el('span', null, value))
    chips.append(c)
  }
  chip(t('chip_format', 'Format'), p.header.versionLabel)
  chip(t('chip_size', 'Size'), fmtBytes(state.bytes.length))
  chip(t('chip_fields', 'Fields'), String(state.view.fields.length))
  chip(t('chip_records', 'Records'), String(p.effective.records))

  const enc = state.override || p.encoding.codepage
  chip(t('chip_encoding', 'Encoding'), state.override
    ? t('encoding_forced', ':cp (you chose it)', { cp: enc })
    : p.encoding.detected
      ? t('encoding_detected', ':cp (detected, :pct% sure)', { cp: enc, pct: Math.round(p.encoding.confidence * 100) })
      : enc, p.encoding.detected && p.encoding.confidence < 0.4 ? 'warn' : '')

  if (state.memoName) chip(t('chip_memo', 'Memo'), state.memoName)
  box.append(chips)

  /* Facts and problems are two different lists on purpose. A number that
     changes nothing must not look like damage. */
  if (p.notes.length) {
    const ul = el('ul', 'dbf-notes')
    for (const n of p.notes) ul.append(el('li', null, noteText(n)))
    box.append(el('h4', 'dbf-sub', t('notes_title', 'What the file says about itself')), ul)
  }
  if (p.problems.length || p.fixes.length) {
    const ul = el('ul', 'dbf-fixes')
    for (const f of p.fixes) ul.append(el('li', null, fixText(f)))
    /* A problem that has already been described as a fix must not be repeated.
       The first version compared 'header-length' against 'headerLength'
       lowercased, which never matches, and the panel said the same thing twice
       in a row. */
    const FIX_FOR = { headerLength: 'header-length', fieldTerminator: 'no-terminator', recordLength: 'record-length' }
    const covered = new Set(p.fixes.map(f => FIX_FOR[f.what]).filter(Boolean))
    for (const pr of p.problems.filter(x => !covered.has(x))) {
      ul.append(el('li', null, t('problem_' + pr, pr)))
    }
    box.append(el('h4', 'dbf-sub', t('fixes_title', 'What had to be corrected')), ul)
  }
}

const noteText = n => ({
  'record-count-differs': () => t('note_record_count', 'The header says :header records; the file holds :file. The file wins.', { header: n.header, file: n.file }),
  'record-length-differs': () => t('note_record_length', 'The header says a record is :header bytes; the field widths add up to :fields. The widths win.', { header: n.header, fields: n.fields }),
  'header-longer-than-field-list': () => t('note_header_longer', 'The header is longer than the field list — normal for Visual FoxPro, which keeps a backlink area there.'),
  'no-eof-marker': () => t('note_no_eof', 'The file has no end marker. Harmless on its own.'),
  /* The reason comes out of the engine in English; the sentence around it is
     translated, so the reason has to be too or the panel reads half-English. */
  'codepage-detected': () => t('note_codepage', 'The file does not say which encoding its text is in. Detected :cp — :why.', {
    cp: n.codepage,
    why: /^valid UTF-8/.test(n.why || '')
      ? t('why_utf8', 'the bytes are valid UTF-8')
      : t('why_scored', 'it produced the most plausible text of the candidates tried'),
  }),
  'partial-record': () => t('note_partial', 'The last :bytes bytes are half a record — the file was cut short.', { bytes: n.bytes }),
}[n.code] || (() => n.code))()

const fixText = f => ({
  headerLength: () => t('fix_header_length', 'Header length was :from, which puts every record at the wrong offset. Corrected to :to.', { from: f.from, to: f.to }),
  fieldTerminator: () => t('fix_terminator', 'The marker that ends the field list was missing; the list end was found from the field widths.'),
  recordLength: () => t('fix_record_length', 'Record length taken from the field widths (:to) instead of the header (:from).', { to: f.to, from: f.from }),
}[f.what] || (() => f.what))()

function currentRows() {
  const opts = { includeDeleted: true, limit: state.offset + PAGE }
  const all = []
  let i = 0
  for (const r of readRecords(state.bytes, state.view.header, state.view.fields, opts)) {
    if (i++ < state.offset) continue
    if (r.deleted && !state.showDeleted) continue
    all.push(r)
    if (all.length >= PAGE) break
  }
  for (const r of all) {
    for (const [k, v] of Object.entries(r.values)) {
      if (v && typeof v === 'object' && 'memoRef' in v) {
        const got = readMemoText(state.memo, v, state.view.header.codepage)
        r.values[k] = got.text != null ? got.text
          : got.missing ? { missingMemo: true }
            : got.error ? { memoError: got.error } : ''
      }
    }
  }
  return all
}

function renderTable() {
  const box = $('#dbf-table')
  box.innerHTML = ''
  const rows = currentRows()
  const fields = state.view.fields

  const meta = el('p', 'dbf-table-meta')
  const shown = Math.min(state.offset + rows.length, state.total)
  meta.textContent = t('table_meta', 'rows :from–:to of :total', { from: state.offset + 1, to: shown, total: state.total })
  box.append(meta)

  const wrap = el('div', 'dbf-scroll')
  const table = el('table', 'dbf-grid')
  const thead = el('thead')
  const hr = el('tr')
  hr.append(el('th', 'dbf-th-idx', '#'))
  for (const f of fields) {
    const th = el('th')
    th.append(el('span', 'dbf-col-name', f.name))
    th.append(el('span', 'dbf-col-type', `${f.type}${f.length ? ' ' + f.length : ''}`))
    hr.append(th)
  }
  thead.append(hr)
  table.append(thead)

  const tbody = el('tbody')
  for (const r of rows) {
    const tr = el('tr', r.deleted ? 'is-deleted' : '')
    const idx = el('td', 'dbf-td-idx', String(r.index + 1))
    if (r.deleted) idx.title = t('row_deleted', 'this row was deleted')
    tr.append(idx)
    for (const f of fields) {
      const v = r.values[f.name]
      const td = el('td')
      if (v && typeof v === 'object' && v.missingMemo) {
        td.className = 'dbf-missing'
        td.textContent = t('memo_missing_cell', 'no memo file')
      } else if (v && typeof v === 'object' && v.memoError) {
        td.className = 'dbf-missing'
        td.textContent = v.memoError
      } else if (v === null) {
        td.className = 'dbf-null'
        td.textContent = '—'
      } else if (typeof v === 'boolean') {
        td.textContent = v ? '✓' : '·'
      } else {
        td.textContent = String(v)
      }
      tr.append(td)
    }
    tbody.append(tr)
  }
  table.append(tbody)
  wrap.append(table)
  box.append(wrap)

  if (state.total > PAGE) {
    const nav = el('div', 'dbf-pager')
    const prev = el('button', 'lg-btn lg-btn--ghost', '←')
    prev.type = 'button'
    prev.disabled = state.offset === 0
    prev.addEventListener('click', () => { state.offset = Math.max(0, state.offset - PAGE); renderTable() })
    const next = el('button', 'lg-btn lg-btn--ghost', '→')
    next.type = 'button'
    next.disabled = state.offset + PAGE >= state.total
    next.addEventListener('click', () => { state.offset += PAGE; renderTable() })
    nav.append(prev, el('span', 'dbf-pager-info', `${state.offset + 1}–${shown} / ${state.total}`), next)
    box.append(nav)
  }
}

function renderControls() {
  const box = $('#dbf-controls')
  box.innerHTML = ''
  box.hidden = false

  const del = el('label', 'dbf-toggle')
  const cb = el('input')
  cb.type = 'checkbox'
  cb.checked = state.showDeleted
  cb.addEventListener('change', () => { state.showDeleted = cb.checked; state.offset = 0; renderTable() })
  del.append(cb, el('span', null, t('show_deleted', 'Show deleted rows')))
  box.append(del)

  const encWrap = el('label', 'dbf-enc')
  encWrap.append(el('span', null, t('encoding_label', 'Encoding')))
  const sel = el('select', 'lg-input')
  const seen = new Set()
  const options = ['utf-8', ...new Set(Object.values(LANGUAGE_DRIVER))].filter(c => c && !seen.has(c) && seen.add(c))
  const current = state.override || state.plan.encoding.codepage
  for (const c of options) {
    const o = el('option', null, c)
    o.value = c
    if (c === current) o.selected = true
    sel.append(o)
  }
  sel.addEventListener('change', () => {
    state.override = sel.value
    state.view.header.codepage = sel.value
  renderSummary()
    renderTable()
  })
  encWrap.append(sel)
  box.append(encWrap)

  const exports = el('div', 'dbf-exports')
  const btn = (label, fn, ghost) => {
    const b = el('button', 'lg-btn' + (ghost ? ' lg-btn--ghost' : ''), label)
    b.type = 'button'
    b.addEventListener('click', fn)
    exports.append(b)
    return b
  }
  btn(t('export_csv_intl', 'CSV (comma)'), () => exportCsv('international'))
  btn(t('export_csv_eu', 'CSV (semicolon)'), () => exportCsv('european'), true)
  btn(t('export_json', 'JSON'), exportJson, true)
  btn(t('export_dbf', 'Repaired .dbf'), exportDbf, true)
  box.append(exports)
}

function allRows() {
  const out = []
  for (const r of readRecords(state.bytes, state.view.header, state.view.fields, { includeDeleted: true })) {
    for (const [k, v] of Object.entries(r.values)) {
      if (v && typeof v === 'object' && 'memoRef' in v) {
        const got = readMemoText(state.memo, v, state.view.header.codepage)
        r.values[k] = { memoText: got.text ?? '' }
      }
    }
    out.push(r)
  }
  return out
}

function download(blob, name) {
  if (window.__downloadFile) return window.__downloadFile(blob, name)
  const u = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = u
  a.download = name
  a.click()
  setTimeout(() => URL.revokeObjectURL(u), 4000)
}

const stem = () => state.name.replace(/\.dbf$/i, '') || 'table'

function exportCsv(preset) {
  setStatus(t('exporting', 'Preparing the file…'))
  try {
    const text = toCsv(allRows(), state.view.fields, { ...CSV_PRESETS[preset], deletedColumn: true })
    download(new Blob([text], { type: 'text/csv;charset=utf-8' }), `${stem()}.csv`)
    setStatus('')
  } catch (e) { setStatus(t('err_export', 'Export failed: ') + e.message, 'error') }
}

function exportJson() {
  setStatus(t('exporting', 'Preparing the file…'))
  try {
    const text = toJson(allRows(), state.view.fields, { deletedColumn: true })
    download(new Blob([text], { type: 'application/json' }), `${stem()}.json`)
    setStatus('')
  } catch (e) { setStatus(t('err_export', 'Export failed: ') + e.message, 'error') }
}

function exportDbf() {
  setStatus(t('exporting', 'Preparing the file…'))
  try {
    const { bytes, records } = toDbf(state.bytes, state.plan, {})
    download(new Blob([bytes], { type: 'application/x-dbf' }), `${stem()}-repaired.dbf`)
    setStatus(t('exported_dbf', 'Wrote a table of :n records with a corrected header.', { n: records }), 'ok')
  } catch (e) { setStatus(t('err_export', 'Export failed: ') + e.message, 'error') }
}

/** Companions arrive with the table or after it; either order works. */
function absorbCompanion(file, bytes) {
  const lower = file.name.toLowerCase()
  if (lower.endsWith('.dbt') || lower.endsWith('.fpt')) {
    state.memo = openMemo(bytes, lower.endsWith('.fpt') ? 'fpt' : 'dbt')
    state.memoName = file.name
    return true
  }
  if (lower.endsWith('.cpg')) {
    /* The shapefile world writes the encoding in a .cpg sidecar, because the
       dBASE header has no value for UTF-8. Measured: all three real GIS tables
       in the fixture set are UTF-8 and say so only here. */
    const said = new TextDecoder().decode(bytes).trim().toLowerCase().replace(/^utf8$/, 'utf-8')
    state.cpgName = file.name
    state.override = said
    if (state.view) { state.view.header.codepage = said }
    return true
  }
  return false
}

async function open(files) {
  const list = Array.from(files || [])
  const table = list.find(f => /\.dbf$/i.test(f.name)) || list[0]
  if (!table) return
  /* The daily run is reserved before the work and settled by the outcome. What
     is delivered is a readable table, so a file that will not open costs
     nothing — taking someone's data AND their day's allowance for the same
     failure is what CLAUDE.md names outright. Keyed by file, so re-opening the
     same table is one run. */
  const ticket = window.__toolLimit
    ? window.__toolLimit.begin('dbf-repair', window.__toolLimit.fileKey ? window.__toolLimit.fileKey(table) : null)
    : null
  if (window.__toolLimit && !ticket) return   // the wall is shown by begin()

  setStatus(t('opening', 'Reading the table…'))
  try {
    const bytes = new Uint8Array(await table.arrayBuffer())
    state.bytes = bytes
    state.name = table.name
    state.memo = null
    state.memoName = null
    state.override = null
    state.offset = 0

    for (const f of list) {
      if (f === table) continue
      if (!absorbCompanion(f, new Uint8Array(await f.arrayBuffer()))) continue
    }

    const plan = diagnose(bytes)
    if (plan.fatal) {
      try { window.__stage?.('error') } catch (_) {}
      suggestOtherTool(table, 'dbf-repair', document.querySelector('#dbf-status'), { text: t('suggest_text'), link: t('suggest_link') })
      if (ticket && ticket.abort) ticket.abort()
      setStatus(t('err_open', 'This file could not be read: ') + plan.fatal, 'error')
      return
    }
    state.plan = plan
    state.view = repairedView(bytes, plan)
    if (state.override) state.view.header.codepage = state.override
    state.total = plan.effective.records

    /* Which dBASE dialects and code pages actually arrive, and where the read
     stops. The encoding guess is the part most likely to be wrong on files we
     have never seen — and a wrong guess yields readable-looking nonsense rather
     than an error, so it is never reported by the person who hit it.
     ⚠️ Anchored on the workspace reveal because `renderSummary()` appears three
     times in this file: the first attempt patched the ENCODING-CHANGE handler
     and fired only when someone switched the code page by hand. */
  try {
    const plan = state.plan
    /* Every open, damaged or not — same rule as the other five tools. An
       earlier version gated on `problems` alone and so stayed silent on a
       REPAIRED table, which in this tool records a `fix` rather than a
       problem: the one case the whole thing exists to hear about. */
    if (plan) {
      reportShape({
        format: 'dbf',
        version: String(plan.header.version ?? ''),
        size: state.bytes.length,
        ok: true,
        reason: plan.problems[0] || null,
        flags: plan.fixes.map(f => f.what)
          .concat(plan.problems, plan.notes.map(n => n.code)),
        counts: {
          fields: state.view.fields.length,
          records: plan.effective.records,
          confidence: Math.round((plan.encoding.confidence || 0) * 100),
        },
      })
    }
  } catch (_) { /* telemetry must never break the tool */ }

  try { window.__stage?.('complete') } catch (_) {}
  $('#dbf-workspace').hidden = false
    renderSummary()
    renderControls()
    renderTable()
    if (ticket && ticket.commit) ticket.commit()
    /* The other six tools reported this; these two did not, so their success
       rate was invisible while their failures were not. */
    window.__aeToolComplete?.({ fields: state.view.fields.length, records: state.total })
    setStatus('')
  } catch (e) {
    try { window.__stage?.('error') } catch (_) {}
    /* `table`, not `file`: the wrong-format hint was pasted in with the other tools'
       variable name, and here it threw ReferenceError inside this very catch — the
       person was left on «Reading the table…» with no reason and no hint, the
       rejection went unhandled (Sentry TIMBRICA-FRONTEND-ND, 01.10.2026). */
    suggestOtherTool(table, 'dbf-repair', document.querySelector('#dbf-status'), { text: t('suggest_text'), link: t('suggest_link') })
    if (ticket && ticket.abort) ticket.abort()
    setStatus(t('err_open', 'This file could not be read: ') + e.message, 'error')
  }
}

export function init() {
  const up = document.getElementById('dbfDrop')
  if (up) {
    up.addEventListener('tool-uploader:file', e => { try { window.__stage?.('file_chosen', { mb: Math.round(((e.detail.files?.[0] || e.detail.file)?.size || 0) / 1048576) }) } catch (_) {}; open(e.detail.files && e.detail.files.length ? e.detail.files : [e.detail.file]) })
    up.addEventListener('tool-uploader:clear', () => {
      $('#dbf-workspace').hidden = true
      $('#dbf-summary').hidden = true
      $('#dbf-controls').hidden = true
      setStatus('')
    })
  }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init)
else init()
